import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CalendarRange, CheckCircle2, Coins, Loader2, Plus, Sparkles, Users, Wand2 } from 'lucide-react';
import { useAuth } from '../Auth';
import { apiService } from '../../services/api';
import useKickoffSession from './useKickoffSession';
import useKickoffData from './useKickoffData';
import KickoffCard, { points as pts } from './KickoffCard';
import { Avatar } from '../ProjectPage/ui';
import { projectRoles } from '../ProjectPage/team';
import { iterationColors, sprintShort } from '../ProjectPage/roadmap/colors';
import { periodState, rangeLabel, toDay, todayDay } from '../ProjectPage/roadmap/dates';

// Kickoff d'un projet, en trois étapes sur le même tableau : placer les objectifs dans les sprints, les estimer en
// tokens, leur donner un responsable. À gauche, ce qui reste à placer (et l'ajout d'objectifs) ; à droite, les
// colonnes de l'étape. Nexus propose les tokens (Kev, d'après les objectifs proches) et relit les titres (règle et
// Kev ; Muse reformule ceux qui ne décrivent pas un résultat). Tout se synchronise entre les participants.

const SCALE = [1, 2, 3, 5, 8, 13, 20];
const STEPS = [
  { key: 'sprints', label: 'Sprints', icon: CalendarRange, placed: (o) => Boolean(o.sprint_id), inbox: 'À placer' },
  { key: 'tokens', label: 'Tokens', icon: Coins, placed: (o) => o.effort > 0, inbox: 'À estimer' },
  { key: 'owners', label: 'Responsables', icon: Users, placed: (o) => Boolean(o.owner_id), inbox: 'Sans responsable' },
];

// fluid : les colonnes se partagent la largeur (tokens : toutes les valeurs visibles d'un coup).
const Column = ({ title, subtitle, accent, count, points, onDrop, active, setActive, children, empty, fluid }) => (
  <section onDragOver={(e) => { e.preventDefault(); setActive(true); }} onDragLeave={() => setActive(false)}
    onDrop={(e) => { e.preventDefault(); setActive(false); onDrop(Number(e.dataTransfer.getData('text/plain'))); }}
    className={`flex max-h-full flex-col rounded-2xl border transition ${fluid ? 'min-w-[7rem] flex-1 basis-0' : 'w-72 shrink-0'}
      ${active ? 'border-indigo-300 bg-indigo-50/60' : 'border-slate-200/80 bg-slate-100/60'}`}>
    <header className="px-3 pb-2 pt-3">
      <div className="flex items-baseline gap-2">
        {accent}
        <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800" title={typeof title === 'string' ? title : undefined}>{title}</h3>
        <span className="shrink-0 text-[11px] tabular-nums text-slate-400">{count}{points ? ` · ${pts(points)}` : ''}</span>
      </div>
      {subtitle && <div className="mt-0.5 text-[11px] leading-snug text-slate-500">{subtitle}</div>}
    </header>
    <div className="min-h-[4rem] flex-1 space-y-1.5 overflow-y-auto px-2 pb-2">
      {children}
      {empty && <p className="rounded-xl border border-dashed border-slate-300/80 px-2 py-4 text-center text-[11px] text-slate-400">{empty}</p>}
    </div>
  </section>
);

const KickoffPage = () => {
  const { projectId } = useParams();
  const id = Number(projectId);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user, getAuthHeaders } = useAuth();
  const session = useKickoffSession({ projectId: id, user, getAuthHeaders });
  const data = useKickoffData(id, { refreshToken: session.refreshToken, notify: session.notifyRefresh || (() => {}) });
  const { project, objectives, iterations, employees, circles, focus, reviews, estimates, actions } = data;
  const step = STEPS.some((s) => s.key === params.get('etape')) ? params.get('etape') : 'sprints';
  const current = STEPS.find((s) => s.key === step);
  const [hovered, setHovered] = useState(null);
  const [newName, setNewName] = useState('');
  const [showPast, setShowPast] = useState(false);
  const [extraOwners, setExtraOwners] = useState([]);
  const [finishing, setFinishing] = useState(false);
  // Projet en proposition : terminer le kickoff le passe « En cours » (Rémy, 2026-10-21).
  const finish = async () => {
    if (!window.confirm(`Terminer le kickoff : « ${project?.name || 'le projet'} » passe En cours.`)) return;
    setFinishing(true);
    try {
      await apiService.patch(`/projects/${id}`, { status: 'in_progress' });
      navigate(`/project/${id}`);
    } catch (err) {
      setFinishing(false);
      window.alert(err?.message || 'Le projet n’a pas pu passer en cours.');
    }
  };
  const [progress, setProgress] = useState(null); // { label, done, total }
  const estimated = useRef(false);
  const today = todayDay();

  const active = useMemo(() => objectives.filter((o) => o.status !== 'done'), [objectives]);
  const doneCount = objectives.length - active.length;
  // Les jalons (datés, hors sprint, sans tokens) ne comptent que pour l'étape des responsables.
  const forStep = (key) => (key === 'owners' ? active : active.filter((o) => !o.is_milestone));
  const open = useMemo(() => forStep(step), [active, step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sprints (avec leur itération et sa couleur) ; responsables possibles.
  const colors = useMemo(() => iterationColors(iterations), [iterations]);
  const sprints = useMemo(() => iterations.flatMap((it) => it.sprints.map((s, index) => ({
    ...s, iteration: it, color: colors.get(it.id), short: sprintShort({ ...s, name: s.description }, index),
    state: periodState(toDay(s.start_date), toDay(s.end_date), today),
  }))).sort((a, b) => String(a.start_date).localeCompare(String(b.start_date))), [iterations, colors, today]);
  const sprintById = useMemo(() => new Map(sprints.map((s) => [s.id, s])), [sprints]);
  const employeeById = useMemo(() => new Map(employees.map((e) => [e.id, e])), [employees]);
  const team = useMemo(() => {
    const ids = new Set();
    projectRoles(id, focus, circles, employees).forEach((role) => role.members.forEach((m) => ids.add(Number(m.id))));
    open.forEach((o) => { if (o.owner_id) ids.add(Number(o.owner_id)); });
    extraOwners.forEach((e) => ids.add(e));
    return [...ids].map((e) => employeeById.get(e)).filter(Boolean).sort((a, b) => a.name.localeCompare(b.name));
  }, [id, focus, circles, employees, open, extraOwners, employeeById]);

  const sprintOptions = sprints.map((s) => ({ value: s.id, label: `${s.description || s.short} · ${rangeLabel(toDay(s.start_date), toDay(s.end_date))}` }));
  const ownerOptions = employees.filter((e) => e.is_active !== false).sort((a, b) => a.name.localeCompare(b.name)).map((e) => ({ value: e.id, label: e.name }));
  const tokenOptions = SCALE.map((v) => ({ value: v, label: pts(v) }));

  // Nexus estime les objectifs sans tokens une fois en arrivant (Kev, moins d'une seconde chacun).
  useEffect(() => {
    if (estimated.current || !objectives.length) return;
    estimated.current = true;
    const todo = objectives.filter((o) => o.status !== 'done' && !o.effort && !o.is_milestone);
    if (todo.length) actions.runAll(todo, actions.estimate);
  }, [objectives, actions]);

  const inbox = open.filter((o) => !current.placed(o)).sort((a, b) => a.id - b.id);
  const placedCount = (s) => forStep(s.key).filter(s.placed).length;
  const proposals = open.filter((o) => !o.effort && o.predicted_tokens);

  const columns = useMemo(() => {
    if (step === 'tokens') {
      // Une colonne « Autre valeur » seulement si un objectif en a une (sinon : « Autre valeur… » dans la pastille).
      const values = SCALE.map((v) => ({ key: v, title: `${v}`, assign: { effort: v }, match: (o) => o.effort === v }));
      return open.some((o) => o.effort > 0 && !SCALE.includes(o.effort))
        ? [...values, { key: 'autre', title: 'Autre', assign: null, match: (o) => o.effort > 0 && !SCALE.includes(o.effort) }]
        : values;
    }
    if (step === 'owners') {
      return team.map((e) => ({ key: e.id, title: e.name, person: e, assign: { owner_id: e.id }, match: (o) => Number(o.owner_id) === e.id }));
    }
    return sprints.filter((s) => showPast || s.state !== 'done' || open.some((o) => o.sprint_id === s.id))
      .map((s) => ({ key: s.id, title: s.description || `Sprint ${s.short}`, sprint: s, assign: { sprint_id: s.id }, match: (o) => o.sprint_id === s.id }));
  }, [step, team, sprints, showPast, open]);
  const pastHidden = sprints.filter((s) => s.state === 'done').length;

  const drop = (column, objectiveId) => {
    const objective = objectives.find((o) => o.id === objectiveId);
    if (!objective) return;
    let fields = column ? column.assign : { sprints: { sprint_id: null }, tokens: { effort: 0 }, owners: { owner_id: null } }[step];
    if (column && !fields) {
      const value = Number(window.prompt('Combien de tokens ?', String(objective.effort || '')));
      if (!value || value < 0) return;
      fields = { effort: value };
    }
    if (column && column.match(objective)) return;
    actions.patch(objective, fields);
  };

  const add = async (event) => {
    event.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setNewName('');
    await actions.create(name);
  };

  const run = async (label, items, action) => {
    setProgress({ label, done: 0, total: items.length });
    await actions.runAll(items, action, (done, total) => setProgress({ label, done, total }));
    setProgress(null);
  };

  const card = (objective) => (
    <KickoffCard key={objective.id} objective={objective} step={step} sprint={sprintById.get(objective.sprint_id)}
      owner={employeeById.get(Number(objective.owner_id))} sprintOptions={sprintOptions} ownerOptions={ownerOptions}
      tokenOptions={tokenOptions} review={reviews[objective.id]} estimate={estimates[objective.id]}
      onPatch={(fields) => actions.patch(objective, fields)} onRemove={() => actions.remove(objective)}
      onReview={() => actions.review(objective)} onAccept={() => actions.acceptSuggestion(objective)}
      onDismiss={() => actions.dismissSuggestion(objective)} />
  );

  const presence = [...new Map((session.connectedUsers || []).map((p) => [String(p.user_id || p.name), p])).values()];

  return (
    <div className="flex h-[calc(100vh-4rem)] min-h-[36rem] flex-col bg-slate-50">
      <div className="mx-auto w-full max-w-[1800px] px-2 pt-3 sm:px-4 lg:px-6">
        <header className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={() => navigate(`/project/${id}`)} aria-label="Retour au projet"
            className="rounded-lg p-1.5 text-slate-400 hover:bg-white hover:text-slate-700"><ArrowLeft size={18} /></button>
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600">Kickoff</p>
            <h1 className="truncate text-xl font-semibold text-slate-900">{project?.name || '…'}</h1>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {project?.status === 'proposition' && (
              <button type="button" onClick={finish} disabled={finishing}
                title="Le projet quitte la proposition et devient un projet en cours"
                className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                {finishing ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Terminer le kickoff
              </button>
            )}
            <span className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${session.realtimeConnected ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${session.realtimeConnected ? 'bg-emerald-500' : 'bg-slate-400'}`} />
              {session.realtimeConnected ? 'En direct' : 'Hors ligne'}
            </span>
            {presence.length > 0 && (
              <div className="flex -space-x-1.5" title={presence.map((p) => p.name).join(', ')}>
                {presence.slice(0, 6).map((p, i) => <Avatar key={`${p.user_id || p.name}-${i}`} name={String(p.name || '?')} picture={p.picture} size="h-7 w-7" />)}
              </div>
            )}
          </div>
        </header>

        <nav className="mt-3 flex flex-wrap items-center gap-1 border-b border-slate-200">
          {STEPS.map((s, index) => {
            const Icon = s.icon;
            const active = s.key === step;
            return (
              <button key={s.key} type="button" onClick={() => setParams(s.key === 'sprints' ? {} : { etape: s.key })}
                className={`flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium transition ${active ? 'border-indigo-600 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
                <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold ${active ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-600'}`}>{index + 1}</span>
                <Icon size={15} className={active ? 'text-indigo-600' : 'text-slate-400'} />
                {s.label}
                <span className="text-xs tabular-nums text-slate-400">{placedCount(s)}/{forStep(s.key).length}</span>
              </button>
            );
          })}
          <span className="ml-auto pb-2 text-xs text-slate-400">
            {active.reduce((sum, o) => sum + (Number(o.effort) || 0), 0)} pts estimés{doneCount ? ` · ${doneCount} terminés masqués` : ''}
          </span>
        </nav>
        {data.error && (
          <p className="mt-2 flex justify-between rounded-lg bg-rose-50 px-3 py-1.5 text-sm text-rose-700">
            {data.error}<button type="button" onClick={data.clearError} className="text-xs font-semibold">Fermer</button>
          </p>
        )}
      </div>

      <div className="mx-auto flex min-h-0 w-full max-w-[1800px] flex-1 gap-3 px-2 py-3 sm:px-4 lg:px-6">
        {/* Ce qui reste à placer pour l'étape, et l'ajout d'objectifs. */}
        <aside onDragOver={(e) => { e.preventDefault(); setHovered('inbox'); }} onDragLeave={() => setHovered(null)}
          onDrop={(e) => { e.preventDefault(); setHovered(null); drop(null, Number(e.dataTransfer.getData('text/plain'))); }}
          className={`flex shrink-0 flex-col rounded-2xl border bg-white shadow-sm transition ${step === 'tokens' ? 'w-72' : 'w-80'} ${hovered === 'inbox' ? 'border-indigo-300 ring-2 ring-indigo-100' : 'border-slate-200/80'}`}>
          <form onSubmit={add} className="border-b border-slate-100 p-3">
            <div className="flex items-center gap-1.5">
              <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nouvel objectif : un résultat vérifiable…"
                className="min-w-0 flex-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-50" />
              <button type="submit" disabled={!newName.trim()} aria-label="Ajouter"
                className="rounded-lg bg-slate-900 p-1.5 text-white hover:bg-slate-700 disabled:opacity-30"><Plus size={16} /></button>
            </div>
            <p className="mt-1.5 text-[11px] text-slate-400">Nexus relit le titre et propose des tokens.</p>
          </form>
          <div className="flex items-center justify-between gap-2 px-3 pt-3">
            <h2 className="text-sm font-semibold text-slate-800">{current.inbox} <span className="font-normal text-slate-400">· {inbox.length}</span></h2>
            <button type="button" disabled={Boolean(progress) || !open.length} title="Nexus relit tous les titres et propose une reformulation quand un objectif ne décrit pas un résultat"
              onClick={() => run('Nexus relit les titres', active, actions.review)}
              className="flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-40">
              <Sparkles size={12} /> Relire les titres
            </button>
          </div>
          {step === 'tokens' && proposals.length > 0 && (
            <div className="mx-3 mt-2 flex items-center justify-between gap-2 rounded-lg bg-indigo-50 px-2.5 py-2 text-xs text-indigo-800">
              <span>{proposals.length} proposition{proposals.length > 1 ? 's' : ''} de Nexus</span>
              <button type="button" onClick={() => proposals.forEach((o) => actions.patch(o, { effort: o.predicted_tokens }))}
                className="flex items-center gap-1 rounded-md bg-indigo-600 px-2 py-1 font-semibold text-white hover:bg-indigo-700">
                <Wand2 size={12} /> Tout appliquer
              </button>
            </div>
          )}
          {progress && (
            <p className="mx-3 mt-2 flex items-center gap-1.5 text-[11px] text-indigo-600">
              <Loader2 size={11} className="animate-spin" /> {progress.label}… {progress.done}/{progress.total}
            </p>
          )}
          <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto p-3">
            {inbox.map(card)}
            {!inbox.length && (
              <p className="rounded-xl border border-dashed border-slate-200 px-3 py-6 text-center text-xs text-slate-400">
                {open.length ? 'Tout est placé pour cette étape.' : 'Pas encore d\'objectif : ajoute le premier ci-dessus.'}
              </p>
            )}
          </div>
        </aside>

        {/* Colonnes de l'étape. */}
        <div className="flex min-w-0 flex-1 flex-col">
          {step === 'sprints' && pastHidden > 0 && (
            <label className="mb-2 flex cursor-pointer items-center gap-1.5 self-end text-xs text-slate-500">
              <input type="checkbox" checked={showPast} onChange={(e) => setShowPast(e.target.checked)} className="rounded border-slate-300" />
              Sprints passés ({pastHidden})
            </label>
          )}
          {step === 'owners' && (
            <label className="relative mb-2 flex cursor-pointer items-center gap-1 self-end text-xs font-semibold text-slate-500 hover:text-slate-800">
              <Plus size={13} /> Ajouter quelqu'un
              <select value="" onChange={(e) => e.target.value && setExtraOwners((list) => [...list, Number(e.target.value)])}
                aria-label="Ajouter quelqu'un" className="absolute inset-0 cursor-pointer opacity-0">
                <option value="">Choisir…</option>
                {ownerOptions.filter((o) => !team.some((t) => t.id === o.value)).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
          )}
          <div className={`flex min-h-0 flex-1 overflow-x-auto pb-2 ${step === 'tokens' ? 'gap-2' : 'gap-3'}`}>
            {columns.map((column) => {
              const items = open.filter(column.match).sort((a, b) => a.id - b.id);
              const points = items.reduce((sum, o) => sum + (Number(o.effort) || 0), 0);
              const s = column.sprint;
              return (
                <Column key={column.key} title={column.title} count={items.length} points={step !== 'tokens' ? points : null} fluid={step === 'tokens'}
                  active={hovered === column.key} setActive={(on) => setHovered(on ? column.key : null)}
                  onDrop={(objectiveId) => drop(column, objectiveId)}
                  accent={s ? <span className={`h-2 w-2 shrink-0 translate-y-[-1px] rounded-full ${s.state === 'current' ? s.color.dot : 'bg-slate-300'}`} />
                    : column.person ? <Avatar name={column.person.name} picture={column.person.picture} size="h-5 w-5" className="translate-y-[3px]" />
                      : step === 'tokens' ? <Coins size={13} className="translate-y-[1px] text-slate-400" /> : null}
                  subtitle={s ? (
                    <>
                      <span className={s.state === 'current' ? 'font-medium text-emerald-700' : ''}>
                        {s.iteration.name} · {rangeLabel(toDay(s.start_date), toDay(s.end_date))}{s.state === 'current' ? ' · en cours' : s.state === 'done' ? ' · passé' : ''}
                      </span>
                      {s.vision && <span className="mt-0.5 line-clamp-2 block text-slate-400">{s.vision}</span>}
                    </>
                  ) : step === 'tokens' && column.key !== 'autre' ? `${pts(points)} au total` : null}
                  empty={!items.length ? 'Glisse un objectif ici' : null}>
                  {items.map(card)}
                </Column>
              );
            })}
            {!columns.length && (
              <p className="m-auto max-w-sm text-center text-sm text-slate-400">
                {step === 'sprints' ? 'Pas encore de sprint : crée-les dans la roadmap du projet.' : 'Personne n\'a encore de rôle sur ce projet : ajoute quelqu\'un.'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KickoffPage;
